import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * The status capsule, transcribed from the design.
 *
 * ── Why a component and not a Badge variant ─────────────────────────────────
 *
 * shadcn's Badge has `default | secondary | destructive | outline`, which is a
 * palette. This is a vocabulary: the design maps twenty-odd status WORDS onto
 * five tones, and the mapping is the part worth sharing. "Investigating" is
 * slate and "Open" is amber, and nobody would guess that from a colour prop.
 *
 * So the label picks the tone. A caller passes the status it already has and
 * gets the design's answer, which is how a seat reading "Active" looks the same
 * on the dashboard and in the seat table without either one deciding.
 *
 * ── The measurements ────────────────────────────────────────────────────────
 *
 * From the design: 10.5px text at weight 600 in a 20px capsule, 3px/8px
 * padding. Small enough to look wrong at any other size beside 12.5px body
 * text, which is what the tables are set in.
 *
 * `minWidth` exists because the design sets one: pills in a column are
 * left-aligned capsules of differing widths otherwise, and a ragged right edge
 * down a table column reads as misalignment rather than as content.
 */
export type PillTone = 'positive' | 'caution' | 'negative' | 'info' | 'inert';

/**
 * Status word to tone, taken verbatim from the design's own map.
 *
 * Worth reading before adding to it. The groupings are not what a first guess
 * would produce:
 *
 * - "Open" is CAUTION, not neutral. An open issue is a customer waiting.
 * - "Investigating" is INFO, not caution - somebody is on it, which is calmer
 *   than open, and the design deliberately cools it down.
 * - "Idle" is caution and "Online" positive, so a workstation nobody is at
 *   looks different from one in use at a glance across a lab's seat list.
 * - "Unassigned" is INERT rather than negative. A seat nobody holds is not a
 *   fault, and colouring it red makes every partly-provisioned lab look broken.
 *
 * Keys are matched case-insensitively, so a server sending `waiting_on_user`
 * and a label reading "Waiting on user" resolve alike.
 */
const TONES: Record<string, PillTone> = {
  // Positive - working, settled, done.
  active: 'positive',
  verified: 'positive',
  stable: 'positive',
  resolved: 'positive',
  online: 'positive',
  closed: 'positive',
  approved: 'positive',
  rolled_out: 'positive',

  // Caution - needs somebody, eventually.
  expiring: 'caution',
  expiring_soon: 'caution',
  pending: 'caution',
  open: 'caution',
  rolling: 'caution',
  idle: 'caution',
  medium: 'caution',
  high: 'caution',
  waiting_on_user: 'caution',
  suspended: 'caution',
  grace: 'caution',

  // Negative - stopped.
  expired: 'negative',
  revoked: 'negative',
  critical: 'negative',
  halted: 'negative',
  failed: 'negative',
  blocked: 'negative',
  hard_blocked: 'negative',
  rejected: 'negative',
  urgent: 'negative',

  // Log levels. `error` is negative and `warning` caution, matching everything
  // else that is broken versus everything that wants an eye kept on it. `info`
  // and `debug` are quiet on purpose: a console whose routine lines are as loud
  // as its failures is a console nobody scans.
  error: 'negative',
  warning: 'caution',
  warn: 'caution',
  info: 'info',
  debug: 'inert',
  trace: 'inert',

  // Info - in hand.
  investigating: 'info',
  in_progress: 'info',
  canary: 'info',
  beta: 'info',
  building: 'info',
  signing: 'info',
  uploading: 'info',
  smoke_testing: 'info',

  // Inert - nothing to report.
  unregistered: 'inert',
  low: 'inert',
  superseded: 'inert',
  unassigned: 'inert',
  queued: 'inert',
  draft: 'inert',
};

const TONE_CLASS: Record<PillTone, string> = {
  positive: 'bg-pill-positive text-pill-positive-foreground',
  caution: 'bg-pill-caution text-pill-caution-foreground',
  negative: 'bg-pill-negative text-pill-negative-foreground',
  info: 'bg-pill-info text-pill-info-foreground',
  inert: 'bg-pill-inert text-pill-inert-foreground',
};

/**
 * The tone for a status word.
 *
 * Exported because a few places need the tone without the pill - a row's left
 * border, a count's colour. Unknown words are inert, matching the design's own
 * default: a status we have no opinion about should look like no opinion, not
 * like an error.
 */
export function toneFor(status: string): PillTone {
  return (
    TONES[
      status
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
    ] ?? 'inert'
  );
}

/** `waiting_on_user` reads as "Waiting on user". Left alone if already prose. */
export function statusLabel(status: string): string {
  const spaced = status.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export interface StatusPillProps {
  /**
   * The status as the server states it. Drives both the colour and, unless
   * `children` is given, the text.
   */
  status: string;
  /** Overrides the tone the status would pick. For a genuine exception only. */
  tone?: PillTone;
  /** Overrides the text. The status still picks the tone. */
  children?: ReactNode;
  /** Matches the design's fixed-width pills in table columns. */
  minWidth?: number;
  className?: string;
}

export function StatusPill({ status, tone, children, minWidth, className }: StatusPillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-full px-2 py-[3px] text-center text-[10.5px] leading-none font-semibold whitespace-nowrap',
        TONE_CLASS[tone ?? toneFor(status)],
        className,
      )}
      style={minWidth !== undefined ? { minWidth } : undefined}
    >
      {children ?? statusLabel(status)}
    </span>
  );
}
